import { Config } from 'config/envConfig';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { chatAPI, repoAPI } from './chatApi';
import {
    RepoType,
    ChatFormatType,
    handleHtmlStringToElement,
    renderAnswerStringToElement,
} from '@quyva/common';
import { alert, AlertType, ExpandableTable } from '@quyva/shared-ui';
import axios from 'axios';
import { getUrl } from '../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faPaperPlane,
    faFileAlt,
    faPlus,
    faFolder,
    faDatabase,
    faGlobe,
    faUsers,
    faUser,
    faRobot,
    faDownload,
    faChartPie,
    faTable,
    faTableColumns,
    faList,
    faCamera,
    faCopy,
    faChevronUp,
    faChevronDown,
    faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { validateUploadedFiles } from '../../utils/validateUploadedFiles';

// Helper to get display name based on interface type
const getDisplayName = (message, user) => {
    const { interface: iface, username } = message;
    const baseName = username || user?.email || 'You';

    const interfaceLabels = {
        // Raw interface strings (not InterfaceType.*) so a stale/missing enum
        // value can't collapse to an "undefined" key and break the label.
        product_support: 'Via Product Support',
        email: 'Via Email',
        telegram: 'Via Telegram',
        whatsapp: 'Via WhatsApp',
        api: 'Via API',
        default: baseName,
    };

    return interfaceLabels[iface] || baseName;
};

const Chat = () => {
    const { repoId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [formattingAnswer, setFormattingAnswer] = useState(false);
    const [repo, setRepo] = useState(location.state?.repo || null);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [attachmentsList, setAttachmentsList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
    const [loadingPath, setLoadingPath] = useState(null);
    const [virtualRepos, setVirtualRepos] = useState([]);
    const [selectedVirtualRepoId, setSelectedVirtualRepoId] = useState(null);
    const [showVirtualRepoDropdown, setShowVirtualRepoDropdown] =
        useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const attachmentMenuRef = useRef(null);
    const virtualRepoDropdownRef = useRef(null);

    // Helper to parse UTC timestamp and convert to local time
    const parseUTCTimestamp = (timestamp) => {
        if (!timestamp) return new Date();
        // If timestamp doesn't end with Z or timezone offset, treat as UTC
        const ts = String(timestamp);
        if (!ts.endsWith('Z') && !ts.match(/[+-]\d{2}:\d{2}$/)) {
            return new Date(ts + 'Z');
        }
        return new Date(ts);
    };

    const formatDate = (timestamp) => {
        return parseUTCTimestamp(timestamp).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const formatTime = (timestamp) => {
        return parseUTCTimestamp(timestamp).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    };

    // Helper to detect if content contains HTML tags
    const isHtmlContent = (content) => {
        if (!content || typeof content !== 'string') return false;
        // Check for common HTML tags
        const htmlPattern = /<\/?[a-z][\s\S]*>/i;
        return htmlPattern.test(content);
    };

    // Render message content - handles both plain text, HTML, and markdown
    const renderMessageContent = (content) => {
        if (!content) return null;

        // Use renderAnswerStringToElement which handles HTML, markdown (bold, italic, etc.)
        return renderAnswerStringToElement(content);
    };

    useEffect(() => {
        if (repo) {
            fetchChatHistory();
            fetchVirtualRepos();
        }
    }, [repoId, repo, selectedVirtualRepoId]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Close attachment menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                attachmentMenuRef.current &&
                !attachmentMenuRef.current.contains(event.target)
            ) {
                setShowAttachmentMenu(false);
            }
            if (
                virtualRepoDropdownRef.current &&
                !virtualRepoDropdownRef.current.contains(event.target)
            ) {
                setShowVirtualRepoDropdown(false);
            }
        };

        if (showAttachmentMenu || showVirtualRepoDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAttachmentMenu, showVirtualRepoDropdown]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchVirtualRepos = async () => {
        try {
            const data = await repoAPI.getVirtualReposForParent(repo.repoid);
            setVirtualRepos(data || []);
        } catch (error) {
            console.error('Failed to fetch virtual repos:', error);
            setVirtualRepos([]);
        }
    };

    const hasVirtualRepos = virtualRepos.length > 0;

    const getSelectedVirtualRepoLabel = () => {
        if (selectedVirtualRepoId === null) {
            return repo?.name || 'DEFAULT';
        }
        const selected = virtualRepos.find(
            (vr) => vr.virtual_repo_id === selectedVirtualRepoId
        );
        return selected?.virtual_repo_name || `Config ${selectedVirtualRepoId}`;
    };

    const getTruncatedLabel = (name, maxLength = 12) => {
        if (!name) return '';
        return name.length > maxLength
            ? `${name.substring(0, maxLength)}...`
            : name;
    };

    const fetchChatHistory = async () => {
        try {
            setLoadingHistory(true);
            const repoidToFetch =
                selectedVirtualRepoId === null ||
                selectedVirtualRepoId === undefined
                    ? repo.repoid
                    : selectedVirtualRepoId;
            const historyData = await chatAPI.getQnAHistory(
                user.email,
                repo.name,
                repoidToFetch
            );

            const transformedMessages = [];
            historyData
                .sort(
                    (a, b) =>
                        new Date(a.metadata.timestamp) -
                        new Date(b.metadata.timestamp)
                )
                .forEach((data) => {
                    const metadata = data.metadata;
                    const response = data.response;

                    transformedMessages.push({
                        id: data.qid + '_q',
                        type: 'question',
                        content: metadata.question,
                        timestamp: metadata.timestamp,
                        qid: data.qid,
                        attachments: metadata.attachments || [],
                        attachmentPaths: metadata.attachments || [], // Store full paths for copy functionality
                        username: metadata.user_id, // Store actual user who asked the question
                        interface: metadata.interface, // Store interface type for display
                    });

                    const isMultiple =
                        response.answer.includes('|END OF ANSWER|');
                    const answerContent = isMultiple
                        ? response.answer
                              .split('|END OF ANSWER|')
                              .join('\n\n---\n\n')
                        : response.answer;

                    transformedMessages.push({
                        id: data.qid + '_a',
                        type: 'answer',
                        content: answerContent,
                        timestamp: metadata.timestamp,
                        qid: data.qid,
                        table: response.table,
                    });
                });

            setMessages(transformedMessages);
        } catch (error) {
            console.error('Failed to fetch chat history:', error);
            setMessages([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!inputMessage.trim() || loading) return;

        const questionText = inputMessage.trim();

        // Store both attachment names and paths
        const attachmentNames = uploadedFiles.map((f) => f.name);
        const attachmentPaths = [...attachmentsList]; // Paths from copied questions or existing attachments

        const userMessage = {
            id: Date.now(),
            type: 'question',
            content: questionText,
            timestamp: new Date().toISOString(),
            attachments: attachmentNames,
            attachmentPaths: attachmentPaths, // Store paths for copy functionality
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputMessage('');
        setLoading(true);

        try {
            // Create FormData for file upload support
            const formData = new FormData();
            const repoidToSend =
                selectedVirtualRepoId === null ||
                selectedVirtualRepoId === undefined
                    ? repo.repoid
                    : selectedVirtualRepoId;
            formData.append('repo_id', repoidToSend);
            formData.append('query', questionText);
            formData.append('user_id', user.email);
            formData.append('file_to_chat', '');
            formData.append('interface', 'default');

            // Append uploaded files (if any)
            if (uploadedFiles && uploadedFiles.length > 0) {
                uploadedFiles.forEach((file) => {
                    formData.append('uploaded_files', file);
                });
            }

            // Append already-attached file paths
            if (Array.isArray(attachmentsList) && attachmentsList.length > 0) {
                attachmentsList.forEach((path) => {
                    formData.append('uploaded_files_path', path);
                });
            }
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/qna`
            );

            const qnaResponse = await axios.post(dynamicUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const parsedData = qnaResponse.data;

            if (parsedData.error) {
                throw new Error(parsedData.error);
            }

            const response = parsedData.response;
            const assistantMessage = {
                id: parsedData.qid + '_a',
                type: 'answer',
                content: response.answer,
                timestamp: parsedData.metadata.timestamp,
                qid: parsedData.qid,
                table: response.table,
                attachments: parsedData.metadata.attachments,
            };

            setMessages((prev) => [...prev, assistantMessage]);

            // Clear uploaded files after successful send
            setUploadedFiles([]);
            setAttachmentsList([]);
        } catch (error) {
            console.error('Failed to send message:', error);

            const errorMessage = {
                id: Date.now() + 1,
                type: 'answer',
                content: `Sorry, I encountered an error: ${
                    error.response?.data?.error || error.message
                }. Please try again.`,
                timestamp: new Date().toISOString(),
                isError: true,
            };

            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    const handleBack = () => {
        navigate('/chatapp/repos');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    const toggleAttachmentMenu = () => {
        setShowAttachmentMenu((prev) => !prev);
    };

    const handleFileUpload = () => {
        setShowAttachmentMenu(false);
        fileInputRef.current?.click();
    };

    const handleCameraCapture = () => {
        setShowAttachmentMenu(false);
        cameraInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            await onFileUpload(files);
        }
        e.target.value = null;
    };

    const handleCameraChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            // Validate captured image
            await onFileUpload(files);
        }
        e.target.value = null;
    };

    const onFileUpload = async (files) => {
        setUploading(true);
        try {
            const { errors, validFiles } = await validateUploadedFiles(
                files,
                uploadedFiles
            );

            if (errors.length > 0) {
                alert(AlertType.ERROR, errors.join('\n'));
                return;
            }

            if (validFiles.length > 0) {
                setUploadedFiles((prev) => [...prev, ...validFiles]);
            }
        } catch (err) {
            console.error('Upload error:', err);
            alert(AlertType.ERROR, 'An error occurred during file upload.');
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveFile = (index) => {
        setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // Extract filename from path
    const getFileName = (filePath) => {
        if (!filePath) return '';
        // Handle both forward and backward slashes
        const parts = filePath.split(/[\\/]/);
        return parts[parts.length - 1];
    };

    // Download attachment
    const handleDownloadAttachment = async (filePath) => {
        if (!filePath) return;
        setLoadingPath(filePath);

        try {
            const encodedPath = encodeURIComponent(filePath);
            const encodedToken = btoa(user.token || '');

            // Use Node server's attachment download endpoint
            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/attachments/download?path=${encodedPath}`
            );

            const response = await axios.get(dynamicUrl, {
                responseType: 'blob',
                headers: {
                    Authorization: encodedToken,
                },
                timeout: 120000,
            });

            // Create and trigger the download
            const blob = response.data;
            const filename = filePath.split(/[\/\\]/).pop() || 'download';
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            // Try to extract a helpful message from server if possible
            let message = 'Download failed';
            if (err.response && err.response.data) {
                try {
                    // axios returns blob for non-2xx too; attempt to read as text
                    const text = await err.response.data.text();
                    if (text) message = text;
                } catch (e) {
                    // ignore
                }
            } else if (err.message) {
                message = err.message;
            }

            alert(
                AlertType.ERROR,
                `${message} ${
                    err.response?.status ? `(${err.response.status})` : ''
                }`
            );
            console.error('Download error:', err);
        } finally {
            setLoadingPath(null);
        }
    };

    // Copy question with attachments
    const handleCopyQuestion = (message) => {
        // Set the question text in input
        setInputMessage(message.content);

        // If message has attachments, get their full paths from metadata
        if (message.attachments && message.attachments.length > 0) {
            // Get attachment paths from the message metadata
            // These are the full paths stored when the question was asked
            const attachmentPaths =
                message.attachmentPaths || message.attachments;
            setAttachmentsList(attachmentPaths);
        } else {
            setAttachmentsList([]);
        }

        // Focus on input field
        inputRef.current?.focus();

        // Scroll to bottom to show input
        setTimeout(() => {
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth',
            });
        }, 100);
    };

    // Format answer functions
    const handleFormatAnswer = async (message, formatType) => {
        try {
            setFormattingAnswer(true);
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/formatans`
            );

            const response = await fetch(dynamicUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    email: user.email,
                    foldername: repo.name,
                    chat_format_type: formatType,
                    ques: message.question || message.content,
                    chunk: message.answer || message.content,
                    file_to_chat: '',
                }).toString(),
            });

            const data = await response.json();
            if (data.output) {
                const parsedData = JSON.parse(data.output);
                const formattedAnswer = JSON.parse(parsedData.response);

                // Convert formatted answer to string for display
                let displayContent = '';
                if (
                    formatType === ChatFormatType.LIST &&
                    formattedAnswer.listData
                ) {
                    // Handle list format
                    displayContent = formattedAnswer.listData
                        .map((item, idx) => `${idx + 1}. ${item}`)
                        .join('\n');
                } else if (
                    formatType === ChatFormatType.TABLE &&
                    formattedAnswer.tableData
                ) {
                    // Handle table format - convert to text representation
                    const headers = formattedAnswer.tableData.headers || [];
                    const rows = formattedAnswer.tableData.rows || [];
                    displayContent =
                        headers.join(' | ') +
                        '\n' +
                        headers.map(() => '---').join(' | ') +
                        '\n' +
                        rows.map((row) => row.join(' | ')).join('\n');
                } else if (
                    formatType === ChatFormatType.CHART &&
                    formattedAnswer.chartData
                ) {
                    // Handle chart format - show data as text for now
                    displayContent = JSON.stringify(
                        formattedAnswer.chartData,
                        null,
                        2
                    );
                } else {
                    // Fallback to string representation
                    displayContent =
                        typeof formattedAnswer === 'string'
                            ? formattedAnswer
                            : JSON.stringify(formattedAnswer, null, 2);
                }

                // Find the answer message and update it
                setMessages((prev) =>
                    prev.map((msg) =>
                        msg.id === message.id
                            ? {
                                  ...msg,
                                  content: displayContent,
                                  formatType: formatType,
                                  originalContent: msg.content,
                                  displayRelatedDoc: false,
                              }
                            : msg
                    )
                );

                alert(AlertType.SUCCESS, `Answer formatted as ${formatType}`);
            } else if (data.error || data.detail) {
                throw new Error(data.error || data.detail);
            }
        } catch (error) {
            console.error('Failed to format answer:', error);
            alert(AlertType.ERROR, `Failed to format answer: ${error.message}`);
        } finally {
            setFormattingAnswer(false);
        }
    };

    const handleChartFormat = (message) =>
        handleFormatAnswer(message, ChatFormatType.CHART);
    const handleTableFormat = (message) =>
        handleFormatAnswer(message, ChatFormatType.TABLE);
    const handleListFormat = (message) =>
        handleFormatAnswer(message, ChatFormatType.LIST);

    // Check if file upload should be enabled
    // File upload is only enabled for MASTER_AGENT repos
    const isFileUploadEnabled = repo && repo.repotype === RepoType.MASTER_AGENT;

    const getRepoIcon = (repotype, storageType) => {
        if (storageType === RepoType.EXTRACTED_DB) return faTableColumns;
        if (storageType === RepoType.AUTO_DB) return faTable;
        if (repotype === RepoType.MASTER_AGENT) return faUsers;
        if (repotype === RepoType.GLOBAL) return faFolder;
        if (
            [RepoType.SQLLITE, RepoType.POSTGRESS, RepoType.MSSQL].includes(
                repotype
            )
        )
            return faDatabase;
        return faFolder;
    };

    return (
        <div className='flex flex-col h-screen bg-backgrounds-secondary'>
            {/* Header */}
            <header className='bg-backgrounds-white border-b border-borders-secondary200 flex-shrink-0'>
                <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4'>
                    <div className='flex items-center gap-4'>
                        {/* Back Button */}
                        <button
                            onClick={handleBack}
                            className='p-2 bg-backgrounds-primary100 hover:bg-backgrounds-primary200 rounded-lg transition-colors border border-borders-secondary200'
                            title='Back to repositories'
                        >
                            <FontAwesomeIcon
                                icon={faArrowLeft}
                                className='text-backgrounds-primary400 text-lg'
                            />
                        </button>

                        {/* Repository Info */}
                        <div className='flex items-center flex-1 min-w-0'>
                            <div className='w-10 h-10 rounded-lg bg-backgrounds-primary100 flex items-center justify-center mr-3 flex-shrink-0'>
                                <FontAwesomeIcon
                                    icon={getRepoIcon(
                                        repo?.repotype,
                                        repo?.storage_type
                                    )}
                                    className='text-backgrounds-primary400'
                                />
                            </div>
                            <div className='flex-1 min-w-0'>
                                <h1 className='text-lg font-semibold text-texts-secondary truncate'>
                                    {repo?.name || 'Repository Chat'}
                                </h1>
                                <p className='text-sm text-texts-secondary300'>
                                    Chatting with {repo?.name || 'repository'}'s
                                    model
                                </p>
                            </div>
                        </div>

                        {/* Logo */}
                        <div className='hidden sm:flex items-center'>
                            <img
                                src='/QuyvaLogo.png'
                                alt='Quyva Logo'
                                className='w-8 h-8'
                            />
                        </div>
                    </div>
                </div>
            </header>

            {/* Messages Area */}
            <div className='flex-1 overflow-y-auto px-4 py-6'>
                {messages.length === 0 ? (
                    <div className='flex items-center justify-center h-full'>
                        <div className='text-center'>
                            <FontAwesomeIcon
                                icon={faFileAlt}
                                className='text-6xl text-gray-300 mb-4'
                            />
                            <h3 className='text-xl font-semibold text-texts-secondary mb-2'>
                                Start a conversation
                            </h3>
                            <p className='text-texts-secondary300'>
                                Ask a question about{' '}
                                {repo?.name || 'this repository'}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className='w-full space-y-4'>
                        {messages.map((message, index) => (
                            <div key={index} className='w-full'>
                                {message.type === 'question' ? (
                                    // Question Message (Right)
                                    <div className='flex justify-end w-full'>
                                        <div className='max-w-[85%] bg-backgrounds-primary100 rounded-lg p-3 shadow-sm border border-backgrounds-primary200'>
                                            <div className='flex items-center justify-between mb-1'>
                                                <div className='flex items-center gap-2'>
                                                    <FontAwesomeIcon
                                                        icon={faUser}
                                                        className='text-backgrounds-primary400 text-xs'
                                                    />
                                                    <span className='text-xs font-medium text-texts-secondary truncate'>
                                                        {getDisplayName(
                                                            message,
                                                            user
                                                        )}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() =>
                                                        handleCopyQuestion(
                                                            message
                                                        )
                                                    }
                                                    className='text-texts-secondary300 hover:text-texts-secondary ml-2'
                                                    title='Copy question'
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faCopy}
                                                        className='text-xs'
                                                    />
                                                </button>
                                            </div>
                                            <p className='text-texts-secondary text-sm whitespace-pre-wrap break-words'>
                                                {message.content}
                                            </p>

                                            {/* Display Attachments */}
                                            {message.attachments &&
                                                message.attachments.length >
                                                    0 && (
                                                    <div className='mt-2 flex flex-wrap gap-1.5'>
                                                        {message.attachments.map(
                                                            (
                                                                attachment,
                                                                idx
                                                            ) => {
                                                                const isLoading =
                                                                    loadingPath ===
                                                                    attachment;
                                                                return (
                                                                    <button
                                                                        key={
                                                                            idx
                                                                        }
                                                                        onClick={() =>
                                                                            handleDownloadAttachment(
                                                                                attachment
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isLoading
                                                                        }
                                                                        className='flex items-center gap-1.5 bg-backgrounds-white border border-backgrounds-primary200 rounded px-2 py-1 text-xs hover:bg-backgrounds-primary100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                                                                        title={
                                                                            isLoading
                                                                                ? 'Downloading...'
                                                                                : `Download ${getFileName(
                                                                                      attachment
                                                                                  )}`
                                                                        }
                                                                    >
                                                                        <FontAwesomeIcon
                                                                            icon={
                                                                                faFileAlt
                                                                            }
                                                                            className='text-backgrounds-primary400'
                                                                            style={{
                                                                                fontSize:
                                                                                    '10px',
                                                                            }}
                                                                        />
                                                                        <span className='text-texts-secondary truncate max-w-[120px]'>
                                                                            {getFileName(
                                                                                attachment
                                                                            )}
                                                                        </span>
                                                                        {isLoading ? (
                                                                            <div className='w-2 h-2 border border-backgrounds-primary400 border-t-transparent rounded-full animate-spin' />
                                                                        ) : (
                                                                            <FontAwesomeIcon
                                                                                icon={
                                                                                    faDownload
                                                                                }
                                                                                className='text-backgrounds-primary400'
                                                                                style={{
                                                                                    fontSize:
                                                                                        '9px',
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </button>
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                )}

                                            <div className='flex items-center justify-between mt-2 text-xs text-texts-secondary300'>
                                                <span>
                                                    {formatDate(
                                                        message.timestamp
                                                    )}
                                                </span>
                                                <span>
                                                    {formatTime(
                                                        message.timestamp
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Answer Message (Left)
                                    <div className='flex justify-start w-full'>
                                        <div className='max-w-[85%] bg-backgrounds-secondaryChat rounded-lg p-3 shadow-sm border border-borders-secondary200'>
                                            <div className='flex items-center justify-between mb-1'>
                                                <div className='flex items-center gap-2'>
                                                    <FontAwesomeIcon
                                                        icon={faRobot}
                                                        className='text-backgrounds-primary400 text-xs'
                                                    />
                                                    <span className='text-xs font-medium text-texts-secondary'>
                                                        VR
                                                    </span>
                                                </div>

                                                {/* Format Answer Buttons - Hidden for MASTER_AGENT, GLOBAL, and database repos */}
                                                {index ===
                                                    messages.length - 1 &&
                                                    !loading &&
                                                    repo?.repotype !==
                                                        RepoType.MASTER_AGENT &&
                                                    repo?.repotype !==
                                                        RepoType.GLOBAL &&
                                                    repo?.repotype !==
                                                        RepoType.SQLLITE &&
                                                    repo?.repotype !==
                                                        RepoType.POSTGRESS &&
                                                    repo?.repotype !==
                                                        RepoType.MSSQL && (
                                                        <div className='flex items-center gap-2 bg-backgrounds-secondaryChat rounded px-2 py-1 relative z-10'>
                                                            <button
                                                                onClick={() =>
                                                                    handleChartFormat(
                                                                        message
                                                                    )
                                                                }
                                                                disabled={
                                                                    formattingAnswer
                                                                }
                                                                className='text-backgrounds-primary400 hover:text-backgrounds-primary500 disabled:opacity-50 transition-colors'
                                                                title='Show in chart format'
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faChartPie
                                                                    }
                                                                    className='text-sm'
                                                                />
                                                            </button>
                                                            <button
                                                                onClick={() =>
                                                                    handleTableFormat(
                                                                        message
                                                                    )
                                                                }
                                                                disabled={
                                                                    formattingAnswer
                                                                }
                                                                className='text-backgrounds-primary400 hover:text-backgrounds-primary500 disabled:opacity-50 transition-colors'
                                                                title='Show in tabular format'
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faTable
                                                                    }
                                                                    className='text-sm'
                                                                />
                                                            </button>
                                                            <button
                                                                onClick={() =>
                                                                    handleListFormat(
                                                                        message
                                                                    )
                                                                }
                                                                disabled={
                                                                    formattingAnswer
                                                                }
                                                                className='text-backgrounds-primary400 hover:text-backgrounds-primary500 disabled:opacity-50 transition-colors'
                                                                title='Show in list format'
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faList
                                                                    }
                                                                    className='text-sm'
                                                                />
                                                            </button>
                                                        </div>
                                                    )}
                                            </div>
                                            <div className='text-texts-secondary text-sm whitespace-pre-wrap break-words'>
                                                {renderMessageContent(
                                                    message.content
                                                )}
                                            </div>

                                            {/* Display Table Data for DB repos */}
                                            {message.table &&
                                                message.table.header &&
                                                message.table.data && (
                                                    <div className='mt-3'>
                                                        <ExpandableTable
                                                            columnData={
                                                                message.table
                                                                    .header
                                                            }
                                                            tableData={
                                                                message.table
                                                                    .data
                                                            }
                                                            pageSize={5}
                                                            pagination={false}
                                                            isSmallScreen={
                                                                false
                                                            }
                                                        />
                                                    </div>
                                                )}

                                            <div className='flex items-center justify-between mt-2 text-xs text-texts-secondary300'>
                                                <span>
                                                    {formatDate(
                                                        message.timestamp
                                                    )}
                                                </span>
                                                <span>
                                                    {formatTime(
                                                        message.timestamp
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {loading && (
                            <div className='flex justify-start w-full'>
                                <div className='max-w-[85%] bg-backgrounds-secondaryChat rounded-lg p-3 shadow-sm border border-borders-secondary200'>
                                    <div className='flex items-center gap-2 mb-1'>
                                        <FontAwesomeIcon
                                            icon={faRobot}
                                            className='text-backgrounds-primary400 text-xs'
                                        />
                                        <span className='text-xs font-medium text-texts-secondary'>
                                            VR
                                        </span>
                                    </div>
                                    <div className='flex items-center space-x-2'>
                                        <div className='w-2 h-2 bg-backgrounds-primary400 rounded-full animate-bounce'></div>
                                        <div
                                            className='w-2 h-2 bg-backgrounds-primary400 rounded-full animate-bounce'
                                            style={{ animationDelay: '0.2s' }}
                                        ></div>
                                        <div
                                            className='w-2 h-2 bg-backgrounds-primary400 rounded-full animate-bounce'
                                            style={{ animationDelay: '0.4s' }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* Input Area */}
            <div className='border-t border-borders-secondary200 bg-backgrounds-white flex-shrink-0'>
                <div className='max-w-4xl mx-auto px-4 py-3'>
                    {/* Uploaded Files Preview */}
                    {(uploadedFiles.length > 0 ||
                        attachmentsList.length > 0) && (
                        <div className='mb-2 flex flex-wrap gap-2'>
                            {/* Show newly uploaded files */}
                            {uploadedFiles.map((file, index) => (
                                <div
                                    key={`upload-${index}`}
                                    className='flex items-center gap-2 bg-backgrounds-primary100 border border-backgrounds-primary200 rounded-lg px-3 py-1.5 text-sm'
                                >
                                    <FontAwesomeIcon
                                        icon={faFileAlt}
                                        className='text-backgrounds-primary400 text-xs'
                                    />
                                    <span className='text-texts-secondary truncate max-w-[150px]'>
                                        {file.name}
                                    </span>
                                    <button
                                        onClick={() => handleRemoveFile(index)}
                                        className='text-texts-secondary300 hover:text-texts-red400 transition-colors'
                                        title='Remove file'
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {/* Show copied attachment paths */}
                            {attachmentsList.map((path, index) => (
                                <div
                                    key={`copied-${index}`}
                                    className='flex items-center gap-2 bg-backgrounds-secondaryChat border border-borders-secondary200 rounded-lg px-3 py-1.5 text-sm'
                                >
                                    <FontAwesomeIcon
                                        icon={faCopy}
                                        className='text-backgrounds-primary400 text-xs'
                                    />
                                    <span className='text-texts-secondary truncate max-w-[150px]'>
                                        {getFileName(path)}
                                    </span>
                                    <button
                                        onClick={() =>
                                            setAttachmentsList((prev) =>
                                                prev.filter(
                                                    (_, i) => i !== index
                                                )
                                            )
                                        }
                                        className='text-texts-secondary300 hover:text-texts-red400 transition-colors'
                                        title='Remove attachment'
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className='flex items-center gap-2 bg-backgrounds-secondary border border-borders-secondary200 rounded-lg px-3 py-2'>
                        {/* Attachment Button with Dropdown - Only for non-database repos */}
                        {isFileUploadEnabled && (
                            <div className='relative' ref={attachmentMenuRef}>
                                <button
                                    onClick={toggleAttachmentMenu}
                                    disabled={loading || uploading}
                                    className='text-backgrounds-primary400 hover:text-backgrounds-primary500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0'
                                    title='Attach file'
                                >
                                    <FontAwesomeIcon
                                        icon={faPlus}
                                        className='text-xl'
                                    />
                                </button>

                                {/* Dropdown Menu */}
                                {showAttachmentMenu && (
                                    <div className='absolute bottom-full left-0 mb-2 bg-backgrounds-white border border-borders-secondary200 rounded-lg shadow-lg py-2 min-w-[180px] z-50'>
                                        <button
                                            onClick={handleFileUpload}
                                            className='w-full px-4 py-2 text-left text-texts-secondary hover:bg-backgrounds-primary100 transition-colors flex items-center gap-3'
                                        >
                                            <FontAwesomeIcon
                                                icon={faFileAlt}
                                                className='text-backgrounds-primary400'
                                            />
                                            <span className='text-sm'>
                                                Upload File
                                            </span>
                                        </button>
                                        <button
                                            onClick={handleCameraCapture}
                                            className='w-full px-4 py-2 text-left text-texts-secondary hover:bg-backgrounds-primary100 transition-colors flex items-center gap-3'
                                        >
                                            <FontAwesomeIcon
                                                icon={faCamera}
                                                className='text-backgrounds-primary400'
                                            />
                                            <span className='text-sm'>
                                                Capture Image
                                            </span>
                                        </button>
                                    </div>
                                )}

                                {/* Hidden File Inputs */}
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    multiple
                                    accept='.pdf,.txt,.png,.jpg,.jpeg'
                                    onChange={handleFileChange}
                                    className='hidden'
                                />
                                <input
                                    ref={cameraInputRef}
                                    type='file'
                                    accept='image/*'
                                    capture='environment'
                                    onChange={handleCameraChange}
                                    className='hidden'
                                />
                            </div>
                        )}

                        {/* Input Field */}
                        <input
                            ref={inputRef}
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder={`Ask question from ${
                                repo?.name || 'repository'
                            }'s model here`}
                            disabled={loading}
                            className='flex-1 bg-transparent border-none focus:outline-none text-texts-secondary text-sm placeholder-texts-secondary300 disabled:opacity-50'
                        />

                        {/* Virtual Repo Dropdown - Only show if there are virtual repos */}
                        {hasVirtualRepos && (
                            <div
                                className='relative'
                                ref={virtualRepoDropdownRef}
                            >
                                <button
                                    onClick={() =>
                                        setShowVirtualRepoDropdown(
                                            !showVirtualRepoDropdown
                                        )
                                    }
                                    disabled={loading}
                                    className='flex items-center gap-1 px-3 py-1.5 bg-backgrounds-primary400 text-white rounded-lg hover:bg-backgrounds-primary500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium'
                                    title='Select from virtual repositories configured for this repository'
                                >
                                    <span className='truncate max-w-[100px]'>
                                        {getTruncatedLabel(
                                            getSelectedVirtualRepoLabel()
                                        )}
                                    </span>
                                    <FontAwesomeIcon
                                        icon={
                                            showVirtualRepoDropdown
                                                ? faChevronUp
                                                : faChevronDown
                                        }
                                        className='text-xs ml-1'
                                    />
                                </button>

                                {/* Virtual Repo Dropdown Menu */}
                                {showVirtualRepoDropdown && (
                                    <div className='absolute bottom-full right-0 mb-2 bg-backgrounds-white border border-borders-secondary200 rounded-lg shadow-lg py-2 min-w-[200px] z-50'>
                                        <div className='px-4 py-2 text-xs font-medium text-texts-secondary300 uppercase tracking-wide border-b border-borders-secondary200'>
                                            More Options
                                        </div>
                                        {/* DEFAULT option */}
                                        <button
                                            onClick={() => {
                                                setSelectedVirtualRepoId(null);
                                                setShowVirtualRepoDropdown(
                                                    false
                                                );
                                            }}
                                            className='w-full px-4 py-2 text-left text-texts-secondary hover:bg-backgrounds-primary100 transition-colors flex items-center justify-between'
                                        >
                                            <span className='text-sm'>
                                                DEFAULT
                                            </span>
                                            {selectedVirtualRepoId === null && (
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className='text-backgrounds-primary400 text-sm'
                                                />
                                            )}
                                        </button>
                                        {/* Virtual repo options */}
                                        {virtualRepos.map((vr) => (
                                            <button
                                                key={vr.virtual_repo_id}
                                                onClick={() => {
                                                    setSelectedVirtualRepoId(
                                                        vr.virtual_repo_id
                                                    );
                                                    setShowVirtualRepoDropdown(
                                                        false
                                                    );
                                                }}
                                                className='w-full px-4 py-2 text-left text-texts-secondary hover:bg-backgrounds-primary100 transition-colors flex items-center justify-between'
                                            >
                                                <span className='text-sm truncate'>
                                                    {vr.virtual_repo_name ||
                                                        `Config ${vr.virtual_repo_id}`}
                                                </span>
                                                {selectedVirtualRepoId ===
                                                    vr.virtual_repo_id && (
                                                    <FontAwesomeIcon
                                                        icon={faCheck}
                                                        className='text-backgrounds-primary400 text-sm'
                                                    />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Send Button */}
                        <button
                            onClick={handleSendMessage}
                            disabled={!inputMessage.trim() || loading}
                            className='text-backgrounds-primary400 hover:text-backgrounds-primary500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0'
                            title='Send message'
                        >
                            <FontAwesomeIcon
                                icon={faPaperPlane}
                                className='text-lg'
                            />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Chat;
